import {
  SYMBOL_SEQUENCE_FONT_CHARS,
  SYMBOL_SEQUENCE_FONT_SIZE,
} from "./SymbolSequenceFont";

export const BREADCRUMB_MESSAGE_ASCII_ADVANCE = 6;
export const BREADCRUMB_MESSAGE_SPACE_ADVANCE = 4;
export const BREADCRUMB_MESSAGE_FULL_ADVANCE = SYMBOL_SEQUENCE_FONT_SIZE;
export const BREADCRUMB_MESSAGE_LINE_HEIGHT = SYMBOL_SEQUENCE_FONT_SIZE + 2;
export const BREADCRUMB_MESSAGE_FALLBACK_CHAR = "?";

const ASCII_PRINTABLE_START = 33;
const ASCII_PRINTABLE_END = 126;
const TAB_WIDTH_IN_SPACES = 2;

export interface BreadcrumbMessageLayoutGlyph {
  readonly char: string;
  readonly x: number;
  readonly y: number;
}

export interface BreadcrumbMessageLayoutLine {
  readonly glyphs: readonly BreadcrumbMessageLayoutGlyph[];
  readonly width: number;
}

export interface BreadcrumbMessageLayout {
  readonly lines: readonly BreadcrumbMessageLayoutLine[];
  readonly width: number;
  readonly height: number;
}

export interface BreadcrumbMessageLayoutOptions {
  readonly maxWidth: number;
  readonly asciiAdvance?: number;
  readonly spaceAdvance?: number;
  readonly fullAdvance?: number;
  readonly lineHeight?: number;
  readonly renderableChars?: string | readonly string[];
  readonly fallbackChar?: string;
  readonly additionalRenderableChars?: readonly string[];
}

interface MutableLine {
  readonly glyphs: BreadcrumbMessageLayoutGlyph[];
  cursorX: number;
  width: number;
}

interface LayoutMetrics {
  readonly asciiAdvance: number;
  readonly spaceAdvance: number;
  readonly fullAdvance: number;
  readonly lineHeight: number;
  readonly maxWidth: number;
  readonly renderableChars: ReadonlySet<string>;
  readonly fallbackChar: string;
  readonly additionalRenderableChars: ReadonlySet<string>;
}

export function layoutBreadcrumbMessage(
  text: string,
  options: BreadcrumbMessageLayoutOptions,
): BreadcrumbMessageLayout {
  const metrics = resolveMetrics(options);
  const lines: BreadcrumbMessageLayoutLine[] = [];
  const paragraphs = text.replace(/\r\n/g, "\n").split("\n");

  for (let paragraphIndex = 0; paragraphIndex < paragraphs.length; paragraphIndex += 1) {
    const paragraph = paragraphs[paragraphIndex] ?? "";
    const line = createLine();

    if (paragraph.length === 0) {
      lines.push(finalizeLine(line, lines.length, metrics.lineHeight));
      continue;
    }

    const tokens = tokenizeParagraph(paragraph);
    for (const token of tokens) {
      appendToken(lines, line, token, metrics);
    }

    lines.push(finalizeLine(line, lines.length, metrics.lineHeight));
  }

  const width = lines.reduce((max, line) => Math.max(max, line.width), 0);
  const height =
    lines.length === 0 ? 0 : (lines.length - 1) * metrics.lineHeight + metrics.fullAdvance;

  return {
    lines,
    width,
    height,
  };
}

function resolveMetrics(options: BreadcrumbMessageLayoutOptions): LayoutMetrics {
  const renderableChars = new Set(
    Array.from(
      typeof options.renderableChars === "string"
        ? options.renderableChars
        : options.renderableChars ?? SYMBOL_SEQUENCE_FONT_CHARS,
    ),
  );

  return {
    asciiAdvance: options.asciiAdvance ?? BREADCRUMB_MESSAGE_ASCII_ADVANCE,
    spaceAdvance: options.spaceAdvance ?? BREADCRUMB_MESSAGE_SPACE_ADVANCE,
    fullAdvance: options.fullAdvance ?? BREADCRUMB_MESSAGE_FULL_ADVANCE,
    lineHeight: options.lineHeight ?? BREADCRUMB_MESSAGE_LINE_HEIGHT,
    maxWidth: Math.max(1, options.maxWidth),
    renderableChars,
    fallbackChar: options.fallbackChar ?? BREADCRUMB_MESSAGE_FALLBACK_CHAR,
    additionalRenderableChars: new Set(options.additionalRenderableChars ?? []),
  };
}

function createLine(): MutableLine {
  return {
    glyphs: [],
    cursorX: 0,
    width: 0,
  };
}

function finalizeLine(
  line: MutableLine,
  lineIndex: number,
  lineHeight: number,
): BreadcrumbMessageLayoutLine {
  return {
    glyphs: line.glyphs.map((glyph) => ({
      ...glyph,
      y: lineIndex * lineHeight,
    })),
    width: line.width,
  };
}

function tokenizeParagraph(paragraph: string): readonly string[] {
  return paragraph.match(/[ \t]+|[^ \t]+/g) ?? [];
}

function appendToken(
  lines: BreadcrumbMessageLayoutLine[],
  line: MutableLine,
  token: string,
  metrics: LayoutMetrics,
): void {
  const isWhitespaceToken = /^[ \t]+$/.test(token);

  if (isWhitespaceToken) {
    appendWhitespaceToken(lines, line, token, metrics);
    return;
  }

  const tokenAdvance = measureTokenAdvance(token, metrics);
  if (tokenAdvance <= metrics.maxWidth) {
    if (line.cursorX > 0 && line.cursorX + tokenAdvance > metrics.maxWidth) {
      lines.push(finalizeLine(line, lines.length, metrics.lineHeight));
      resetLine(line);
    }
    appendChars(line, token, metrics);
    return;
  }

  appendWrappedWord(lines, line, token, metrics);
}

function appendWhitespaceToken(
  lines: BreadcrumbMessageLayoutLine[],
  line: MutableLine,
  token: string,
  metrics: LayoutMetrics,
): void {
  if (line.cursorX === 0) {
    return;
  }

  const tokenAdvance = measureTokenAdvance(token, metrics);
  if (line.cursorX + tokenAdvance > metrics.maxWidth) {
    lines.push(finalizeLine(line, lines.length, metrics.lineHeight));
    resetLine(line);
    return;
  }

  appendChars(line, token, metrics);
}

function appendWrappedWord(
  lines: BreadcrumbMessageLayoutLine[],
  line: MutableLine,
  token: string,
  metrics: LayoutMetrics,
): void {
  for (const char of token) {
    const advance = getCharacterAdvance(char, metrics);
    if (line.cursorX > 0 && line.cursorX + advance > metrics.maxWidth) {
      lines.push(finalizeLine(line, lines.length, metrics.lineHeight));
      resetLine(line);
    }
    appendCharacter(line, char, metrics);
  }
}

function appendChars(line: MutableLine, token: string, metrics: LayoutMetrics): void {
  for (const char of token) {
    appendCharacter(line, char, metrics);
  }
}

function appendCharacter(line: MutableLine, char: string, metrics: LayoutMetrics): void {
  const advance = getCharacterAdvance(char, metrics);
  const normalizedChar = normalizeRenderableChar(char, metrics);

  if (normalizedChar !== " " && normalizedChar !== "\t") {
    const renderWidth = metrics.fullAdvance;
    line.glyphs.push({
      char: normalizedChar,
      x: line.cursorX,
      y: 0,
    });
    line.width = Math.max(line.width, line.cursorX + renderWidth);
  }

  line.cursorX += advance;
}

function measureTokenAdvance(token: string, metrics: LayoutMetrics): number {
  let width = 0;
  for (const char of token) {
    width += getCharacterAdvance(char, metrics);
  }
  return width;
}

function getCharacterAdvance(char: string, metrics: LayoutMetrics): number {
  if (char === " ") {
    return metrics.spaceAdvance;
  }
  if (char === "\t") {
    return metrics.spaceAdvance * TAB_WIDTH_IN_SPACES;
  }

  const codePoint = char.codePointAt(0);
  if (codePoint !== undefined && codePoint >= ASCII_PRINTABLE_START && codePoint <= ASCII_PRINTABLE_END) {
    return metrics.asciiAdvance;
  }

  return metrics.fullAdvance;
}

function normalizeRenderableChar(char: string, metrics: LayoutMetrics): string {
  if (char === " " || char === "\t") {
    return char;
  }
  if (metrics.additionalRenderableChars.has(char)) {
    return char;
  }
  if (metrics.renderableChars.has(char)) {
    return char;
  }
  if (metrics.renderableChars.has(metrics.fallbackChar)) {
    return metrics.fallbackChar;
  }
  return " ";
}

function resetLine(line: MutableLine): void {
  line.glyphs.length = 0;
  line.cursorX = 0;
  line.width = 0;
}
